import { useEffect, useState } from 'react'
import { api, fmtDate, fmtSiren } from '../lib/api'
import type { Societe, DossierAnnuel } from '../lib/api'
import { href, navigate } from '../lib/router'
import { IconArrowLeft, IconChevronRight, IconFolder } from '../components/Icons'

type State = {
  societe: Societe | null
  dossiers: DossierAnnuel[] | null
  error: string | null
  loading: boolean
}

export function SocieteDetailPage({ code }: { code: string }) {
  const [state, setState] = useState<State>({
    societe: null, dossiers: null, error: null, loading: true
  })

  useEffect(() => {
    let alive = true
    setState(s => ({ ...s, loading: true, error: null }))
    Promise.all([api.societe(code), api.dossiersAnnuels(code)]).then(([soc, dos]) => {
      if (!alive) return
      if (!soc.ok) {
        setState({ societe: null, dossiers: null, error: soc.error, loading: false })
        return
      }
      setState({
        societe: soc.data,
        dossiers: dos.ok ? dos.data : [],
        error: dos.ok ? null : `Dossiers indisponibles : ${dos.error}`,
        loading: false
      })
    })
    return () => { alive = false }
  }, [code])

  if (state.loading) {
    return <div className="empty-state"><p>Chargement…</p></div>
  }

  if (!state.societe) {
    return (
      <div className="empty-state">
        <h2>Société introuvable</h2>
        <p>{state.error ?? `Aucune société avec le code ${code}.`}</p>
        <p><a href={href('/societes')}>← Retour à la liste</a></p>
      </div>
    )
  }

  const s = state.societe

  return (
    <>
      <button
        type="button"
        className="btn-back"
        onClick={() => navigate('/societes')}
      >
        <IconArrowLeft size={16} />
        <span>Portefeuille</span>
      </button>

      <div className="detail-header">
        <h2 className="detail-title">{s.RaisonSociale}</h2>
        <div className="detail-meta">
          <span className="chip-id">{s.CodeClient}</span>
          {s.FormeJuridiqueAbregee && <span className="chip">{s.FormeJuridiqueAbregee}</span>}
          {s.TypeSociete && <span className="chip">{s.TypeSociete}</span>}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <p className="stat-label">Identification</p>
          <dl className="detail-dl">
            <dt>SIREN</dt><dd>{fmtSiren(s.Siren)}</dd>
            <dt>SIRET</dt><dd>{s.Siret ?? '—'}</dd>
            <dt>TVA intracom</dt><dd>{s.TvaIntra ?? '—'}</dd>
            <dt>Code NAF</dt><dd>{s.CodeNAF ?? '—'}</dd>
          </dl>
        </div>

        <div className="detail-card">
          <p className="stat-label">Adresse</p>
          <dl className="detail-dl">
            <dt>Voie</dt><dd>{s.Adresse1 ?? '—'}</dd>
            <dt>Ville</dt><dd>{s.CodePostal ?? ''} {s.Ville ?? '—'}</dd>
            <dt>Pays</dt><dd>{s.Pays ?? '—'}</dd>
          </dl>
        </div>

        <div className="detail-card">
          <p className="stat-label">Contact</p>
          <dl className="detail-dl">
            <dt>Email</dt><dd>{s.Email ?? '—'}</dd>
            <dt>Téléphone</dt><dd>{s.Telephone ?? '—'}</dd>
            <dt>Mobile</dt><dd>{s.Mobile ?? '—'}</dd>
          </dl>
        </div>

        <div className="detail-card">
          <p className="stat-label">Descriptif</p>
          <p className="detail-descriptif">{s.Descriptif ?? '—'}</p>
        </div>
      </div>

      <section className="section-block">
        <div className="section-header">
          <h3>Dossiers annuels</h3>
          <span className="section-counter">
            {state.dossiers?.length ?? 0} dossier{(state.dossiers?.length ?? 0) > 1 ? 's' : ''}
          </span>
        </div>

        {state.error && (
          <div className="alert alert-warning">{state.error}</div>
        )}

        {state.dossiers && state.dossiers.length === 0 && (
          <div className="empty-state"><p>Aucun dossier annuel pour cette société.</p></div>
        )}

        {state.dossiers && state.dossiers.length > 0 && (
          <ul className="list-linked">
            {state.dossiers.map(d => (
              <li key={d.IdDossierAnnuel}>
                <a
                  href={href(`/societes/${encodeURIComponent(code)}/dossiers/${d.IdDossierAnnuel}`)}
                  className="row-linked"
                >
                  <IconFolder size={18} className="row-linked-icon" />
                  <div className="row-linked-body">
                    <div className="row-linked-title">
                      {d.Libelle}
                      {d.EnCours === 'true' && <span className="chip chip-green" style={{ marginLeft: 8 }}>En cours</span>}
                      {d.DateCloture && <span className="chip chip-grey" style={{ marginLeft: 8 }}>Clôturé</span>}
                    </div>
                    <div className="row-linked-sub">
                      Exercice du {fmtDate(d.DateDebut)} au {fmtDate(d.DateFin)}
                    </div>
                  </div>
                  <IconChevronRight size={16} className="row-linked-chevron" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  )
}
